























































































































































































































































import { Expression, AnyType, isFunction, TypeMap, Traverser, SetExpression, UpdateExpression, GetExpression, ConstantExpression, ObjectType, ChainExpression, Exprs, objectMap, Types, CommandProvider, DataTypes, Func, PathExpression } from 'expangine-runtime';
import { LiveContext, LiveResult, LiveRuntime } from 'expangine-runtime-live';
import { ListOptions } from '../../../common';
import { ExpressionVisuals, ExpressionModifierCallback } from '../ExpressionVisuals';
import { getConfirmation } from '../../../app/Confirm';
import { sendNotification } from '../../../app/Notify';
import { getEditFunction } from '../../../app/EditFunction';
import { getDisplayData } from '../../../app/DisplayData';
import { getProgram } from '../../../app/GetProgram';
import ExpressionBase from '../ExpressionBase';
import { Preferences, PreferenceCategory } from '../../../app/Preference';


const PREF_EXPRESSION_CHANGE = Preferences.define({
  key: 'expression_change',
  label: 'Change expressions without confirmation',
  category: [PreferenceCategory.EXPRESSION, PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_EXPRESSION_MODIFY = Preferences.define({
  key: 'expression_modify',
  label: 'Modify expressions without confirmation',
  category: [PreferenceCategory.EXPRESSION, PreferenceCategory.CONFIRM],
  defaultValue: false,
});


export default ExpressionBase().extend({
  name: 'ExpressionMenu',
  props: {
    text: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
      required: true,
    },
    invalidOverride: {
      type: Boolean,
      default: null,
    },
  },
  data: () => ({
    showTypeInformation: false,
  }),
  computed: {
    isInvalid(): boolean {
      return this.invalidOverride !== null ? this.invalidOverride : this.invalid;
    },
    typeInfoClass(): string {
      return this.invalid ? 'red--text darken-4' : '';
    },
    hasTypeInformation(): boolean {
      return !!(this.requiredType && !(this.requiredType instanceof AnyType))
          || !!(this.computedType && !(this.computedType instanceof AnyType));
    },
    canTransform(): boolean {
      return !this.readOnly;
    },
    canChange(): boolean {
      return !this.readOnly;
    },
    statusColor(): string {
      return this.isInvalid ? this.displayOptions.error : this.displayOptions.color;
    },
    statusDark(): boolean {
      return this.isInvalid;
    },
    statusTooltip(): string {
      if (!this.isInvalid || !this.requiredType || !this.computedType) {
        return this.tooltip;
      }
      const actual = this.registry.getTypeDescribe(this.computedType);
      const expected = this.registry.getTypeDescribe(this.requiredType);

      return `Expected ${expected} but given ${actual}`;
    },
    starters(): ExpressionVisuals[] {
      return this.registry.getExpressionsStart(this.requiredType);
    },
    modifiers(): ListOptions<ExpressionModifierCallback> {
      return this.registry.getExpressionsModifiers(this.requiredType, this.value, this.computedType);
    },
    canCopy(): boolean {
      return this.registry.isValidExpressionCopy(this.value);
    },
    canCopyChain(): boolean {
      return this.value.parent instanceof ChainExpression
        && this.registry.isValidExpressionCopy(this.value.parent);
    },
  },
  methods: {
    async createFunction() {
      const { value, context, registry } = this;

      if (!(context instanceof ObjectType)) {
        await sendNotification({
          message: 'The context to an expression must be an object to create a function here.',
        });

        return false;
      }

      const input: TypeMap = {};
      let setTopLevel = false;

      value.traverse(new Traverser((expr) => 
      {
        if (expr instanceof PathExpression) 
        {
          const e0 = expr.expressions[0];
          const e1 = expr.expressions[1];

          if (expr.parent instanceof SetExpression || expr.parent instanceof UpdateExpression) 
          {
            if (expr.expressions.length === 2 && 
                e0 instanceof GetExpression &&
                e1 instanceof ConstantExpression &&
                e1.value in context.options.props) 
            {
              setTopLevel = true;
            }
          }

          if (e0 instanceof GetExpression &&
              e1 instanceof ConstantExpression &&
              e1.value in context.options.props) 
          {
            input[e1.value] = context.options.props[e1.value];
          }
        }
      }));

      if (setTopLevel) {
        await sendNotification({
          message: 'You cannot create a function where a sub-expression directly sets a variable in the current context.',
        });

        return false;
      }

      const result = await getEditFunction({
        registry,
        func: Func.create(registry.defs, {
          params: Types.object(input),
          expression: Exprs.return(value),
        }),
      });

      if (result) {
        const args = objectMap(input, (_, name) => Exprs.get(name));

        this.input(Exprs.invoke(result.name, args));
      }
    },
    async changeTo(visuals: ExpressionVisuals) {
      if (await getConfirmation({ pref: PREF_EXPRESSION_CHANGE })) {
        this.input(visuals.create(this.requiredType, this.context, this.registry));
      }
    },
    async modify(value: ExpressionModifierCallback) {
      const options = isFunction(value)
        ? undefined
        : value.options;
      const callback = isFunction(value)
        ? value
        : value.handler;

      if (await getConfirmation({ ...options, pref: PREF_EXPRESSION_MODIFY })) {
        const result = callback();
        if (result) {
          this.input(result);
        }
      }
    },
    async edit() {
      const { registry, context, value: program, requiredType: expectedType } = this;

      const result = await getProgram({
        title: 'Edit Expression',
        registry,
        context,
        program,
        expectedType,
        programLabel: 'Expression',
        confirm: 'Replace',
      });

      if (!result)
      {
        return await sendNotification({ message: 'Edit Expression canceled.' });
      }

      this.input(result.program);
    },
    async getAll() {
      const registry = this.registry;
      const data = DataTypes.copy(this.data);
      const results: Map<string, [any, number]> = new Map();
      const resultType = this.computedTypeRaw;
      const capture: Expression = this.value;
      let root: Expression = capture;

      if (!resultType || data === null) {
        return;
      }

      while (root.parent) {
        root = root.parent;
      }

      const interceptProvider: CommandProvider<LiveContext, LiveResult> = 
      {
        returnProperty: LiveRuntime.returnProperty,
        getFunction: (name) => LiveRuntime.getFunction(name),
        getOperation: (id) => LiveRuntime.getOperation(id),
        getComputed: (id) => LiveRuntime.getComputed(id),
        getOperationScopeDefaults: (id) => LiveRuntime.getOperationScopeDefaults(id),
        getCommand(expr: Expression) 
        {
          const run = LiveRuntime.getCommand(expr, interceptProvider);

          if (expr === capture)
          {
            return (context: LiveContext) => 
            {
              const result = run(context);
              const resultJson = resultType.toJson(result);
              const resultCopy = resultType.fromJson(resultJson);
              const resultKey = JSON.stringify(resultJson);
              const resultPair = results.get(resultKey);

              if (!resultPair)
              {
                results.set(resultKey, [resultCopy, 1]);
              }
              else
              {
                resultPair[1]++;
              }

              return result;
            };
          }

          return run;
        },
      };

      const command = interceptProvider.getCommand(root);

      try 
      {
        command(data);

        getDisplayData({
          registry,
          data: Array.from(results.values()).map(([result, times]) => ({
            result,
            times,
          })),
        });
      } 
      catch (e) 
      {
        window.console.log('error in getAll', e);
      }
    },
    menuRight(ev: KeyboardEvent) {
      if (ev.keyCode === 39) {
        for (const refName in this.$refs) {
          const ref = this.$refs[refName] as any;

          if (ref && 
            ref.$parent && 
            ref.$parent.mouseEnterHandler &&
            ref.$el.classList.contains('v-list-item--highlighted')) {
            ref.$parent.mouseEnterHandler();
          }
        }
      }
    },
  },
});
